import * as amplitude from "@amplitude/analytics-browser";
import { Icon, Text } from "@chakra-ui/react";
import _ from "lodash";
import { useState } from "react";
import { RiRefreshLine } from "react-icons/ri";
import { FormStatusMenu } from "src/components/Menus/FormStatusMenu";
import { Glossary } from "src/components/Text/Glossary";
import { getBulkFormStatusDialogCopy } from "src/constants";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useLazyRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { isNotNull } from "src/services/predicates";
import * as GQL from "src/types/graphql";
import { FETCH_FORM_STATUS } from "../graphql/queries";
import { useFormStatusUpdates } from "../hooks/useFormStatusUpdates";
import { SchoolFormId } from "../types";
import { trackFormAction, trackFormActionError } from "./trackFormAction";

interface Props {
  fetchSelectedFormKeys: () => Promise<SchoolFormId[]>;
  refetchForms: () => Promise<unknown>;
}

const MAX_COUNT = 30000;

export const FormStatusMenuBulk = ({
  fetchSelectedFormKeys,
  refetchForms,
}: Props) => {
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);
  const [uniqueStatus, setUniqueStatus] = useState<GQL.form_status_enum>();
  const toast = useAvelaToast();

  const { updateFormStatus, isLoading } = useFormStatusUpdates();

  const [fetchAppStatus] = useLazyRemoteDataQuery<
    GQL.FetchFormStatus,
    GQL.FetchFormStatusVariables
  >(FETCH_FORM_STATUS, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
  });

  const handleOnClick = async (): Promise<boolean> => {
    const appKeys = await fetchSelectedFormKeys();
    const appIds = _.uniq(appKeys.map((key) => key.formId).filter(isNotNull));

    setSelectedAppIds(appIds);

    if (appIds.length > MAX_COUNT) {
      setUniqueStatus(undefined);
    }

    if (appIds.length <= MAX_COUNT) {
      const results = await fetchAppStatus({
        variables: { form_ids: appIds },
      });
      const statuses = results.data?.form.map((app) => app.status) || [];

      setUniqueStatus(statuses.length === 1 ? statuses[0] : undefined);
    }

    return true;
  };

  const { confirm, confirmationDialog, setBody, setHeader } =
    useConfirmationDialog({
      header: null,
      body: null,
      cancelButton: {
        label: "Cancel",
      },
      confirmButton: {
        label: "Yes, change status",
        colorScheme: "blue",
      },
      translate: true,
    });

  const handleOnSelectStatusChange = async (value: GQL.form_status_enum) => {
    const copy = getBulkFormStatusDialogCopy(value, selectedAppIds.length);
    setHeader(
      <Text>
        <Glossary>{copy.header}</Glossary>
      </Text>
    );
    setBody(
      <Text>
        <Glossary>{copy.body}</Glossary>
      </Text>
    );

    amplitude.track("Clicked bulk update form status", {
      form_ids: selectedAppIds,
      status: value,
    });
    if (await confirm()) {
      amplitude.track("Confirmed bulk update form status", {
        form_ids: selectedAppIds,
        status: value,
      });

      try {
        await updateFormStatus(selectedAppIds, value);
        await refetchForms();

        toast({ title: "Form status updated" });
        trackFormAction({
          verb: "Completed",
          actionType: "SET_FORM_STATUS",
          bulk: true,
          count: selectedAppIds.length,
          async: false,
          extra: { status: value },
        });
      } catch (err) {
        trackFormActionError(err, {
          actionType: "SET_FORM_STATUS",
          bulk: true,
          count: selectedAppIds.length,
          async: false,
          extra: { status: value },
        });
        toast.error({ title: "Error updating form status" });
      }
    } else {
      amplitude.track("Canceled bulk update form status", {
        count: selectedAppIds.length,
        status: value,
      });
    }
  };

  return (
    <>
      <FormStatusMenu
        formStatus={uniqueStatus}
        label="Update status"
        leftIcon={<Icon as={RiRefreshLine} />}
        onSelect={handleOnSelectStatusChange}
        onClick={handleOnClick}
        isLoading={isLoading}
      />
      {confirmationDialog}
    </>
  );
};
